import React from "react";
import {
  RefreshCw, CheckCircle2, XCircle, Link2, ShieldCheck,
} from "lucide-react";

export default function AdminPendingList({
  pending,
  loading,
  onRefresh,
  onApprove,
  onReject,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="font-display font-black text-2xl tracking-tighter">
            Review Queue
          </h2>
          <span className="badge-neutral" data-testid="admin-queue-count">
            {pending.length} pending
          </span>
        </div>
        <button
          onClick={onRefresh}
          className="text-xs uppercase tracking-widest font-semibold hover:text-red-600 flex items-center gap-2"
          data-testid="admin-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {pending.length === 0 ? (
        <div
          className="border border-dashed border-zinc-300 p-12 text-center"
          data-testid="admin-queue-empty"
        >
          <ShieldCheck size={28} className="mx-auto text-zinc-400" />
          <div className="label-eyebrow mt-3">Queue empty</div>
          <div className="font-display font-bold text-lg mt-1">
            Paste an article above to ingest new cases.
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-queue-list">
          {pending.map((p) => (
            <PendingRow key={p.id} p={p} onApprove={onApprove} onReject={onReject} />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingRow({ p, onApprove, onReject }) {
  return (
    <div
      className="border border-zinc-900 bg-white"
      data-testid={`admin-pending-${p.id}`}
    >
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between border-b border-red-600">
        <div>
          <div className="label-eyebrow text-red-500">{p.case_id}</div>
          <div className="font-display font-black text-xl tracking-tight mt-0.5">
            {p.name}
          </div>
        </div>
        <span className="badge-neutral bg-red-600 text-white border-red-600">
          {p.crime_type}
        </span>
      </div>
      <div className="p-5 grid grid-cols-2 gap-3 text-sm">
        <InfoLine label="Jurisdiction" value={`${p.city || "?"}, ${p.state_code || "?"}`} />
        <InfoLine label="Year" value={p.conviction_year} />
        <InfoLine label="Sentence" value={p.sentence} span />
        <InfoLine label="Animals" value={p.animals_affected || "—"} span />
      </div>
      <div className="px-5 pb-4">
        <div className="label-eyebrow mb-1">Summary</div>
        <div className="text-sm">{p.crime_summary}</div>
        {p.description && (
          <div className="text-xs text-zinc-600 mt-2 leading-relaxed">
            {p.description}
          </div>
        )}
        {p.source_url && (
          <a
            href={p.source_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold hover:text-red-600"
          >
            <Link2 size={12} /> Source
          </a>
        )}
      </div>
      <div className="border-t border-zinc-200 flex">
        <button
          onClick={() => onApprove(p.id)}
          className="flex-1 py-3 text-xs uppercase tracking-widest font-bold bg-zinc-900 text-white hover:bg-red-600 flex items-center justify-center gap-2"
          data-testid={`admin-approve-${p.id}`}
        >
          <CheckCircle2 size={14} /> Approve & Publish
        </button>
        <button
          onClick={() => onReject(p.id)}
          className="flex-1 py-3 text-xs uppercase tracking-widest font-bold text-zinc-700 hover:bg-zinc-100 flex items-center justify-center gap-2 border-l border-zinc-200"
          data-testid={`admin-reject-${p.id}`}
        >
          <XCircle size={14} /> Reject
        </button>
      </div>
    </div>
  );
}

function InfoLine({ label, value, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-semibold mt-0.5">{value || "—"}</div>
    </div>
  );
}
